// The customizable quick-access bar — which pages it can hold, and which ones
// a given person actually sees.
//
// Feedback #835 (James, 2026-09-30): "Can we make the [quick bar] with
// calendar, matters, time entries and to do customizable so users can put
// what they want there?"
//
// The catalog is DERIVED from lib/navItems.js rather than restated: a bar item
// is a shortcut into the sidebar, so it carries the sidebar entry's label,
// icon, path, permission and feature flag, and "can this person open it" is
// the sidebar's own rule (canOpenNavItem).  A page added to the sidebar is
// pickable here with no second edit — and the server's allowlist
// (api/src/lib/quickBarItems.js QUICK_BAR_KEYS) is held equal to the sidebar
// by api/src/tests/quickBar.test.js, so all three stay one list.
//
// A KEY is the page's path without the leading slash: 'calendar',
// 'time-entries', 'court-rules'.
//
// Which bar a person gets (the contract shared with the API):
//   their own quick_bar_items  ->  the firm's quick_bar_default  ->  the
//   built-in default below
// then filtered to the pages THIS person can open.  The server stores keys
// without checking permissions (the bar is navigation only and every page
// enforces its own access), so an admin's firm default may name a page some
// staff cannot open — for them it simply does not render.

import { navGroups, canOpenNavItem } from './navItems'

export const QUICK_BAR_MAX = 8

// Used when neither the person nor the firm has chosen a bar.  The four pages
// of the #831/#832 bar first, in their old order, then the other daily-work
// pages (2026-10).  visibleQuickBarItems() still drops any this person cannot
// open, and it is within QUICK_BAR_MAX.  Must equal the server's
// QUICK_BAR_BUILTIN_DEFAULT (api/src/lib/quickBarItems.js); the API's
// quickBar.test.js parses this declaration and compares, so keep it a
// single-quoted array literal under this name.
export const QUICK_BAR_BUILTIN_DEFAULT = Object.freeze([
  'calendar', 'matters', 'time-entries', 'tasks', 'clients', 'reports', 'leads',
])

// A bar button carries the sidebar entry's own name.  /tasks is "To-Do" in
// the sidebar too since 2026-10 (it was "Tasks & To-Do" there, with a
// bar-only override), so the bar needs no label of its own; To-Do keeps its
// due-soon badge wherever it sits.  lib/todoName.test.js holds the sidebar,
// the bar and Ctrl+K to one name.

export const keyForPath = (path) => String(path || '').replace(/^\//, '')

export const QUICK_BAR_CATALOG = Object.freeze(navGroups.flatMap((group) =>
  group.items.map((item) => {
    const key = keyForPath(item.path)
    return Object.freeze({
      key,
      label: item.name,
      icon: item.icon,
      path: item.path,
      permission: item.permission,
      feature: item.feature,
      group: group.label,
    })
  })
))

export const QUICK_BAR_KEYS = Object.freeze(QUICK_BAR_CATALOG.map((i) => i.key))

const BY_KEY = new Map(QUICK_BAR_CATALOG.map((i) => [i.key, i]))

export const quickBarItemFor = (key) => BY_KEY.get(key) || null

// A stored value as the client should read it: an array of known keys, first
// occurrence kept, at most QUICK_BAR_MAX — or null for "not chosen, fall
// through to the next default".  The server validates on write, so this
// mostly guards against a page that has since left the sidebar, and against
// a stale local cache.  An empty array stays empty: it is a real choice (the
// person wants no shortcuts), not "unset".
export function normalizeQuickBarKeys (value) {
  if (!Array.isArray(value)) return null
  const seen = new Set()
  const out = []
  for (const key of value) {
    if (typeof key !== 'string' || !BY_KEY.has(key) || seen.has(key)) continue
    seen.add(key)
    out.push(key)
  }
  return out.slice(0, QUICK_BAR_MAX)
}

// The keys this person's bar is built from, before the permission filter.
export function effectiveQuickBarKeys (userItems, firmDefault) {
  const own = normalizeQuickBarKeys(userItems)
  if (own !== null) return own
  const firm = normalizeQuickBarKeys(firmDefault)
  if (firm !== null) return firm
  return [...QUICK_BAR_BUILTIN_DEFAULT]
}

// Catalog entries for `keys`, in order, keeping only pages this person can
// open (same permission + feature-flag rule as the sidebar).
export function visibleQuickBarItems (keys, user, isFeatureOn) {
  const out = []
  for (const key of normalizeQuickBarKeys(keys) || []) {
    const item = BY_KEY.get(key)
    if (item && canOpenNavItem(user, item, isFeatureOn)) out.push(item)
  }
  return out
}

// Every page this person could add, grouped like the sidebar, minus `exclude`
// (the keys already on their bar).  Groups left empty are dropped.
export function pickableQuickBarGroups (user, isFeatureOn, exclude = []) {
  const skip = new Set(exclude)
  const groups = []
  for (const item of QUICK_BAR_CATALOG) {
    if (skip.has(item.key) || !canOpenNavItem(user, item, isFeatureOn)) continue
    let g = groups[groups.length - 1]
    if (!g || g.label !== item.group) { g = { label: item.group, items: [] }; groups.push(g) }
    g.items.push(item)
  }
  return groups
}

// A default as words, e.g. "Calendar, Matters, Time Entries, To-Do, Clients,
// Reports, Leads" for QUICK_BAR_BUILTIN_DEFAULT.
export function describeQuickBarKeys (keys) {
  const items = (normalizeQuickBarKeys(keys) || []).map((k) => BY_KEY.get(k).label)
  return items.length ? items.join(', ') : 'no pages'
}

// Fired on `window` whenever a quick bar is saved, so every mounted bar in
// this window updates without a reload.  detail: { items?, firmDefault? } —
// a key that is absent means "unchanged".
export const QUICK_BAR_CHANGED_EVENT = 'lextrack-quick-bar-changed'
